import { useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';

import type { TvDashboard, TvDevice } from 'src/api/zrm';
import { fetchTvDevice } from 'src/tv/tvClient';
import { TV_REFRESH_MS, useFailingSince, useUnpairedOn } from 'src/tv/tvQueries';

/**
 * Re-checks `GET /tv/me` on the dashboard's beat for the screen's name and country, and hands each
 * answer to `onDevice`. A 401 goes back to pairing. Returns when the current outage began, or null.
 */
export const useTvDevice = (
  dashboard: TvDashboard,
  token: string,
  device: TvDevice | null,
  onDevice: (device: TvDevice) => void,
  onUnpaired: () => void,
): number | null => {
  const { data, error, failureCount } = useQuery({
    queryKey: ['tv', 'me', dashboard, token],
    // The signal aborts the request when the screen unmounts (a re-pair), so no answer for the old token lands.
    queryFn: ({ signal }) => fetchTvDevice(dashboard, token, signal),
    // Right after pairing the device is fresh, so the first check waits a minute; after a reload
    // (no device yet) it is looked up at once.
    initialData: device ?? undefined,
    staleTime: TV_REFRESH_MS,
  });

  useEffect(() => {
    if (data) onDevice(data);
  }, [data, onDevice]);

  useUnpairedOn(error, onUnpaired);

  return useFailingSince(failureCount);
};
